import React, { useState } from "react";
import {
  useWSModalCustom,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSForm,
  WSInfoBox,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSControlGroup,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayeeEngagementType, WorkLog } from "@wingspanhq/wingspan-api-sdk";
import { queriesWorkLogs } from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import { PublicCommentField } from "../../components/PublicCommentField";
import { useMutationCancelWorkLogConvertedResource } from "../../hooks/useMutationCancelWorkLogConvertedResource";
import { selectorWorkLogConvertedResourceLabels } from "../../selectors/selectorWorkLogConvertedResourceLabels";
import { selectorWorkLogWorkerNoun } from "../../selectors/selectorWorkLogWorkerNoun";

type ReopenOption = "InReview" | "InProgress";

type FormData = {
  comment: string;
};

type Props = {
  workLog: WorkLog;
  contractorName?: string;
  /**
   * A discarded work log is auto-submitted, which only happens on the W-2 side,
   * so getting this right matters most on exactly the reopen path that has no
   * converted resource to name.
   */
  engagementType?: PayeeEngagementType | null;
} & WSModalCustomProps<{
  success?: boolean;
}>;

export const ModalReopenWorkLog: React.FC<Props> = ({
  workLog,
  contractorName = "the contractor",
  engagementType,
  ...modalProps
}) => {
  const { openSnackbar } = useWSSnackbar();
  // A W-2 work log converts into a pay statement, a 1099 one into an invoice.
  // A discarded work log converted into nothing at all — there the cancel step
  // is skipped and only the work-log transition runs.
  // Only opened on an approved or discarded work log; an approved one always
  // carries its converted type, a discarded one has nothing to name.
  const { noun } = selectorWorkLogConvertedResourceLabels({
    convertedResourceType: workLog.convertedResourceType,
    engagementType
  });
  const workerNoun = selectorWorkLogWorkerNoun(engagementType);
  const hasConvertedResource = !!workLog.convertedResourceId;

  // The v1 combined payable-cancel (which also set the work log to InReview) is
  // split into a v3 cancel + a follow-up work-log transition. For a pay
  // statement the spec guarantees the first half — "cancelling detaches the
  // statement's work logs (back to InReview)".
  // TODO(v3): `cancelPayable` documents no such side effect, so on the
  // contractor side a work log may stay Approved after "Make edits yourself".
  const {
    cancelConvertedResource,
    isLoading: isCancelling,
    error: cancelError
  } = useMutationCancelWorkLogConvertedResource();
  const [requestChanges, requestChangesMeta] =
    queriesWorkLogs.requestWorkLogEdit.useMutation();
  const [reopenOption, setReopenOption] = useState<ReopenOption>("InReview");

  const isLoading = isCancelling || requestChangesMeta.isLoading;

  const onSuccess = () => {
    modalProps.onClose({ success: true });
    openSnackbar({
      icon: { name: "check" },
      message:
        reopenOption === "InReview" && hasConvertedResource
          ? "Work log reopened for your edits."
          : `Work log returned to ${workerNoun} for corrections.`
    });
  };

  const handleReopen = async (formData: FormData) => {
    const comment = formData.comment || undefined;

    // Step 1: Cancel the converted resource (no-op when there is none). On
    // failure the modal stays open with the server's own reason below: a cancel
    // is rejected for reasons the payer needs to read (the pay statement has
    // been paid, its payroll run is finalized), not for reasons retrying fixes.
    const cancelled = await cancelConvertedResource(workLog, comment);

    if (!cancelled) {
      return;
    }

    // Step 2: Transition the work log to its reopened status.
    //
    // A work log that never converted (a discarded one) has no cancel to lean
    // on, so the transition is the only thing that reopens it — hence the
    // explicit request-edit call rather than the InReview shortcut below.
    // `voidInvoiceRequest` is optional, so this runs with or without a comment.
    if (reopenOption === "InProgress" || !hasConvertedResource) {
      requestChanges(
        {
          workLogId: workLog.id!,
          voidInvoiceRequest: comment ? { reason: comment } : undefined
        },
        { onSuccess }
      );
      return;
    }

    // TODO(v3): "Make edits yourself" (InReview) reopen — cancelling the
    // converted resource is what returns the work log to InReview, and the payer
    // comment can't be persisted on v3 (updateWorkLog takes only
    // notes/attachments; transitions take no comment). Comment dropped.
    onSuccess();
  };

  const cancelClause = hasConvertedResource
    ? `The ${noun} will be cancelled. `
    : "";

  return (
    <WSModal title="Reopen for changes" {...modalProps}>
      <WSForm<FormData> onSubmit={handleReopen} defaultValues={{ comment: "" }}>
        <WSList gap="XL">
          <WSText.ParagraphSm color="gray500">
            {hasConvertedResource
              ? `Are you sure you want to cancel the ${noun} and reopen for changes?`
              : "Are you sure you want to reopen this work log for changes?"}
          </WSText.ParagraphSm>

          {/* "Make edits yourself" leaves the work log In-review, which is a
              side effect of cancelling the converted resource. With nothing to
              cancel there is only one reopen path, so the choice is dropped. */}
          {hasConvertedResource ? (
            <WSControlGroup
              type="radio"
              name="reopenOption"
              chip
              value={reopenOption}
              onChange={(value: string) => {
                setReopenOption(value as ReopenOption);
              }}
              options={[
                {
                  label: "Make edits yourself",
                  helperText: `${cancelClause}The work log will be reopened for changes and re-approval under the “To approve” tab in work logs.`,
                  value: "InReview"
                },
                {
                  label: `Return to ${workerNoun} for corrections`,
                  helperText: `${cancelClause}The work log will be returned to the ${workerNoun} for changes and resubmission under the “In-progress” tab in work logs.`,
                  value: "InProgress"
                }
              ]}
            />
          ) : (
            <WSText.ParagraphSm color="gray500">
              {`The work log will be returned to the ${workerNoun} for changes and resubmission under the “In-progress” tab in work logs.`}
            </WSText.ParagraphSm>
          )}

          {hasConvertedResource && (
            <WSInfoBox title="Funded for payment">
              {`If the ${noun} has been funded for payment already, the funds are returned to the payroll funding account when it is cancelled.`}
            </WSInfoBox>
          )}

          <PublicCommentField
            label="Comment (optional)"
            description={`Comment is visible to both you and the ${workerNoun}. Please provide specific details that will inform ${contractorName} about the decision.`}
            placeholder="Provide specific details about the decision"
          />

          <WSAlert theme="warning" title="Reopen for changes" icon="alert">
            This action cannot be undone
          </WSAlert>

          <ErrorAlert
            context="WorkLog"
            error={cancelError ?? requestChangesMeta.error}
          />

          <WSActions
            alignment="fill"
            buttons={[
              {
                kind: "Secondary",
                label: "Cancel",
                type: "button",
                onClick: () => {
                  modalProps.onClose({ success: false });
                }
              },
              {
                kind: "Primary",
                destructive: true,
                label: hasConvertedResource
                  ? `Cancel ${noun} and reopen work log`
                  : "Reopen work log",
                loading: isLoading
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSModal>
  );
};

export const useModalReopenWorkLog = () => useWSModalCustom(ModalReopenWorkLog);
